import { Avatar, Chip } from '@heroui/react';
import { BiSearch } from 'react-icons/bi';
import { ACTIONED_BY_OPTIONS } from '@/config';
import { AsyncNormalSelect, type ISelectItem, SearchInput, SearchTextArea, Select } from '@/core/ui';
import { useContentReview } from '@/store/content-review';
import { stringToNumberArr } from '@/utils/string';

const STATE_FILTER_OPTIONS: ISelectItem[] = [
  { key: 'all', label: 'All' },
  { key: '0', label: 'Unresolved' },
  { label: 'No action needed', key: 1 },
  { label: 'Deleted', key: 2 },
  { label: 'Amended', key: 3 },
];

export const QuestionReviewFiltersSection = () => {
  const search = useContentReview.use.search();
  const recordIds = useContentReview.use.recordIds();
  const createdBy = useContentReview.use.createdBy();
  const actionedBy = useContentReview.use.actionedBy();
  const setSearch = useContentReview.use.setSearch();
  const setRecordIds = useContentReview.use.setRecordIds();
  const setCreatedBy = useContentReview.use.setCreatedBy();
  const setActionedBy = useContentReview.use.setActionedBy();
  const resetFilters = useContentReview.use.resetFilters();
  const state = useContentReview.use.state();
  const setState = useContentReview.use.setState();
  const hasFilters = Boolean(search || recordIds?.length > 0 || createdBy != null || actionedBy != null);

  const handleStateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value || value === 'all') {
      setState(null);
      return;
    }
    setState(Number(value));
  };

  const handleActionedByChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value || value === 'all') {
      setActionedBy(null);
      return;
    }
    setActionedBy(Number(value));
  };
  return (
    <div className='bg-background rounded-lg shadow-md p-4'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-lg font-medium text-default-700'>Filters</h1>
        {hasFilters && (
          <Chip variant='bordered' onClose={() => resetFilters()}>
            Reset
          </Chip>
        )}
      </div>

      <div className='flex flex-wrap gap-4 items-end'>
        <SearchInput
          isClearable
          type='search'
          className='w-72'
          placeholder='Search review comments...'
          startContent={<BiSearch />}
          classNames={{ inputWrapper: 'h-12' }}
          variant='faded'
          value={search}
          onClear={() => setSearch('')}
          onValueChange={(value) => setSearch(value)}
        />

        <SearchTextArea
          placeholder='Record ID'
          labelPlacement='outside'
          value={recordIds?.join(', ') ?? ''}
          onValueChange={(value) => setRecordIds(stringToNumberArr(value))}
          variant='faded'
          className='w-72'
          maxRows={1}
          size='lg'
        />

        <AsyncNormalSelect
          // apiSearch={false}
          isVirtualized={false}
          addValuePrefix={false}
          // label='Editor'
          name='editorId'
          size='lg'
          variant='faded'
          transform={(a) => Number.parseInt(a, 10)}
          placeholder='Members ..'
          className='w-64'
          queryKey='admins'
          groupBy='options'
          // multiple={true}
          selectionMode='single'
          maxSelected={1}
          selectedKeys={createdBy != null ? [String(createdBy)] : undefined}
          onSelectionChange={(value) => {
            const val = Array.from(value).join('');
            setCreatedBy(val ? Number(val) : null);
          }}
          renderItem={(item) => (
            <div className='flex gap-2 items-center'>
              <Avatar
                alt={item.label}
                className='shrink-0'
                classNames={{
                  base: 'bg-default-200 border-default-400',
                  name: 'uppercase',
                  // icon: 'text-black/80',
                }}
                name={item.label
                  .split(' ')
                  .map((word) => word[0])
                  .join('')}
                size='sm'
                src={''}
              />
              <div className='flex flex-col'>
                <span className='text-xs'>{item.label}</span>
                {item.description && item.description.split(' ').length > 1 && (
                  <div className='flex flex-col w-40'>
                    {item.description.split(' ').map((word) => (
                      <span key={word} className='text-xs text-default-400'>
                        {word}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        />

        <Select
          size='lg'
          options={STATE_FILTER_OPTIONS}
          selectedKeys={[String(state ?? 'all')]}
          onChange={handleStateFilterChange}
          popoverProps={{ classNames: { content: ' !p-1.5 !w-40' } }}
          classNames={{ listbox: '!p-0', innerWrapper: '!capitalize' }}
          placeholder='Status'
          name='status'
          className='rounded w-40'
          variant='faded'
        />
        <Select
          size='lg'
          options={ACTIONED_BY_OPTIONS}
          selectedKeys={[String(actionedBy ?? 'all')]}
          onChange={handleActionedByChange}
          popoverProps={{ classNames: { content: ' !p-1.5 !w-40' } }}
          classNames={{ listbox: '!p-0', innerWrapper: '!capitalize' }}
          placeholder='Actioned by'
          name='actionedBy'
          className='rounded w-40'
          variant='faded'
        />
      </div>
    </div>
  );
};
